import { c as _c } from "react/compiler-runtime";
import { IcoPencil16 } from '@onefootprint/icons';
import { cx } from 'class-variance-authority';
import { type KeyboardEvent, useEffect, useRef, useState } from 'react';
type InlineEditableTextProps = {
  value: string;
  fallback?: string;
  placeholder: string;
  ariaLabel: string;
  canEdit?: boolean;
  autoFocus?: boolean;
  editIconAriaLabel?: string;
  inputClassName?: string;
  buttonClassName?: string;
  textClassName?: string;
  emptyTextClassName?: string;
  shouldCloseOnBlur?: (value: string) => boolean;
  onChange: (value: string) => void;
};
const InlineEditableText = t0 => {
  const $ = _c(40);
  const {
    value,
    fallback: t1,
    placeholder,
    ariaLabel,
    canEdit: t2,
    autoFocus: t3,
    editIconAriaLabel,
    inputClassName,
    buttonClassName,
    textClassName,
    emptyTextClassName: t4,
    shouldCloseOnBlur: t5,
    onChange
  } = t0;
  const fallback = t1 === undefined ? "" : t1;
  const canEdit = t2 === undefined ? true : t2;
  const autoFocus = t3 === undefined ? false : t3;
  const emptyTextClassName = t4 === undefined ? "text-tertiary" : t4;
  const shouldCloseOnBlur = t5 === undefined ? _temp : t5;
  const [isEditing, setIsEditing] = useState(autoFocus);
  const inputRef = useRef(null);
  const displayValue = value || fallback;
  let t6;
  let t7;
  if ($[0] !== isEditing) {
    t6 = () => {
      if (!isEditing) {
        return;
      }
      inputRef.current?.focus();
      inputRef.current?.select();
    };
    t7 = [isEditing];
    $[0] = isEditing;
    $[1] = t6;
    $[2] = t7;
  } else {
    t6 = $[1];
    t7 = $[2];
  }
  useEffect(t6, t7);
  let t8;
  if ($[3] !== canEdit) {
    t8 = () => {
      if (canEdit) {
        setIsEditing(true);
      }
    };
    $[3] = canEdit;
    $[4] = t8;
  } else {
    t8 = $[4];
  }
  const startEditing = t8;
  let t9;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      setIsEditing(false);
    };
    $[5] = t9;
  } else {
    t9 = $[5];
  }
  const stopEditing = t9;
  let t10;
  if ($[6] !== shouldCloseOnBlur || $[7] !== value) {
    t10 = () => {
      if (shouldCloseOnBlur(value)) {
        stopEditing();
      } else {
        inputRef.current?.focus();
      }
    };
    $[6] = shouldCloseOnBlur;
    $[7] = value;
    $[8] = t10;
  } else {
    t10 = $[8];
  }
  const handleBlur = t10;
  let t11;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = event => {
      if (event.key !== "Enter") {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      stopEditing();
      event.currentTarget.blur();
    };
    $[9] = t11;
  } else {
    t11 = $[9];
  }
  const handleKeyDown = t11;
  if (isEditing) {
    let t12;
    if ($[10] !== inputClassName) {
      t12 = cx("h-7 w-full rounded border border-tertiary bg-primary px-2 text-label-3 text-primary outline-none focus:border-accent", inputClassName);
      $[10] = inputClassName;
      $[11] = t12;
    } else {
      t12 = $[11];
    }
    const t13 = !canEdit;
    let t14;
    if ($[12] !== onChange) {
      t14 = event_0 => onChange(event_0.target.value);
      $[12] = onChange;
      $[13] = t14;
    } else {
      t14 = $[13];
    }
    let t15;
    if ($[14] !== ariaLabel || $[15] !== handleBlur || $[16] !== placeholder || $[17] !== t12 || $[18] !== t13 || $[19] !== t14 || $[20] !== value) {
      t15 = <input aria-label={ariaLabel} ref={inputRef} className={t12} value={value} placeholder={placeholder} disabled={t13} onChange={t14} onBlur={handleBlur} onKeyDown={handleKeyDown} />;
      $[14] = ariaLabel;
      $[15] = handleBlur;
      $[16] = placeholder;
      $[17] = t12;
      $[18] = t13;
      $[19] = t14;
      $[20] = value;
      $[21] = t15;
    } else {
      t15 = $[21];
    }
    return t15;
  }
  const t12 = canEdit && "hover:bg-hover focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent";
  let t13;
  if ($[22] !== buttonClassName || $[23] !== t12) {
    t13 = cx("flex min-w-0 max-w-full items-center gap-1 rounded px-1 py-0.5 text-left text-label-3 text-primary", "disabled:cursor-not-allowed", t12, buttonClassName);
    $[22] = buttonClassName;
    $[23] = t12;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  const t14 = !canEdit;
  const t15 = !value && emptyTextClassName;
  let t16;
  if ($[25] !== t15 || $[26] !== textClassName) {
    t16 = cx("truncate", textClassName, t15);
    $[25] = t15;
    $[26] = textClassName;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] !== displayValue || $[29] !== t16) {
    t17 = <span className={t16}>{displayValue}</span>;
    $[28] = displayValue;
    $[29] = t16;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] !== canEdit || $[32] !== editIconAriaLabel) {
    t18 = canEdit && <span className="shrink-0 opacity-0 transition-opacity group-hover/inline-edit:opacity-100 max-md:opacity-100"><IcoPencil16 color="tertiary" aria-label={editIconAriaLabel} aria-hidden={!editIconAriaLabel} /></span>;
    $[31] = canEdit;
    $[32] = editIconAriaLabel;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  let t19;
  if ($[34] !== startEditing || $[35] !== t13 || $[36] !== t14 || $[37] !== t17 || $[38] !== t18) {
    t19 = <div className="group/inline-edit flex w-full min-w-0 items-center gap-1"><button type="button" className={t13} disabled={t14} onClick={startEditing}>{t17}{t18}</button></div>;
    $[34] = startEditing;
    $[35] = t13;
    $[36] = t14;
    $[37] = t17;
    $[38] = t18;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  return t19;
};
export default InlineEditableText;
function _temp() {
  return true;
}